"use client";

import { useState } from "react";
import Breadcrumb from "components/Breadcrumb";
import PodcastAudioPlayer from "components/PodcastAudioPlayer";
import SafeHTML from "components/SafeHTML";
import AuthorModal from "components/AuthorModal";
import ShareSection from "components/ShareSection";
import { buildCdnUrl } from "lib/metadata";
import styles from "./page.module.css";

function formatDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}.${month}.${day}`;
}

const extractAudioValue = (podcast) => {
  if (!podcast || typeof podcast !== "object") return "";

  const candidates = [
    podcast.audio,
    podcast.audioFile,
    podcast.audioUrl,
    podcast.file,
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;
    if (typeof candidate === "string") return candidate;
    if (typeof candidate === "object") {
      const nested =
        candidate.url ||
        candidate.path ||
        candidate.src ||
        candidate.file ||
        candidate.name;
      if (nested) return nested;
    }
  }

  return "";
};

export default function PodcastDetailClient({ podcast, detailUrl }) {
  const [showAuthorModal, setShowAuthorModal] = useState(false);

  const audioUrl = buildCdnUrl(extractAudioValue(podcast));
  const shareText = podcast.name || "Podcast";

  const breadcrumbItems = [
    { label: "Нүүр хуудас", url: "/" },
    { label: "Podcast", url: "/podcast" },
    { label: podcast.name || "Дэлгэрэнгүй", url: null },
  ];

  return (
    <>
      {audioUrl && (
        <section
          className={styles.audioHero}
          style={
            podcast?.pictures && podcast.pictures[0]
              ? { backgroundImage: `url(${buildCdnUrl(podcast.pictures[0])})` }
              : undefined
          }
        >
          <div className="container">
            <div className={styles.audioHeroInner}>
              <PodcastAudioPlayer
                src={audioUrl}
                title={podcast.name || "Podcast audio"}
              />
            </div>
          </div>
        </section>
      )}

      <div className="container py-4">
        <Breadcrumb items={breadcrumbItems} />

        <article className={styles.article}>
          <div className={styles.header}>
            {podcast.authors && (
              <div className={styles.authorSection}>
                <div
                  className={styles.authorClickable}
                  onClick={() => setShowAuthorModal(true)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) =>
                    e.key === "Enter" && setShowAuthorModal(true)
                  }
                >
                  {podcast.authors.avatar && (
                    <img
                      src={buildCdnUrl(podcast.authors.avatar)}
                      alt={podcast.authors.name || "Нийтлэгч"}
                      width={60}
                      height={60}
                      className={styles.authorAvatar}
                      loading="lazy"
                      decoding="async"
                    />
                  )}
                  <div className={styles.authorInfo}>
                    <span className={styles.authorName}>
                      {podcast.authors.name}
                    </span>
                    {podcast.authors.title && (
                      <span className={styles.authorTitle}>
                        {podcast.authors.title}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <h1 className={styles.title}>{podcast.name}</h1>

          {podcast.createdAt && (
            <p className={styles.date}>{formatDate(podcast.createdAt)}</p>
          )}

          {podcast.details && (
            <div className={styles.content}>
              <SafeHTML html={podcast.details} className={styles.htmlContent} />
            </div>
          )}

          {podcast.links && podcast.links.length > 0 && (
            <div className={styles.linksSection}>
              <h2 className={styles.sectionTitle}>Сувгууд</h2>
              <div className={styles.linksList}>
                {podcast.links.map((link, index) => (
                  <a
                    key={link._id || `${link.name || "link"}-${index}`}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.linkItem}
                  >
                    {link.name || link.url}
                  </a>
                ))}
              </div>
            </div>
          )}

          <ShareSection detailUrl={detailUrl} shareText={shareText} />
        </article>
      </div>

      <AuthorModal
        author={podcast.authors}
        open={showAuthorModal}
        onClose={() => setShowAuthorModal(false)}
      />
    </>
  );
}
